<template>
  <div class="container-fluid py-3">
    <div class="d-flex align-items-center mb-4">
      <h4 class="mb-0 mr-3">Portfolio Dashboard</h4>
      <button class="btn btn-sm btn-outline-secondary" @click="refresh" :disabled="refreshing">
        {{ refreshing ? 'Refreshing…' : '↻ Refresh Prices' }}
      </button>
    </div>

    <PageLoader v-if="refreshing && !lastUpdated" message="Fetching live prices…" />

    <!-- Chain summary cards -->
    <div class="row mb-4" v-if="!refreshing || lastUpdated">
      <div class="col-6 col-md-4 col-lg-2 mb-2" v-for="s in chainSummaries" :key="s.chain">
        <div class="card h-100 text-center" :style="`border-top: 3px solid ${s.color}`">
          <div class="card-body p-2">
            <div class="font-weight-bold" :style="`color:${s.color}`">{{ s.label }}</div>
            <div class="text-muted small" v-if="s.total !== null">${{ s.total }}</div>
            <div class="text-muted small" v-else>
              <router-link :to="'/' + s.chain">Connect Ledger</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Total portfolio value -->
    <div class="alert">
      <strong>Total Portfolio Value:</strong>
      <span class="total-val">{{ fmtUsd(totalUsd) }}</span>
      <span class="price-source ml-2">via Binance</span>
      <span class="text-muted small ml-3">{{ lastUpdated ? 'Updated ' + lastUpdated : 'never' }}</span>
    </div>

    <!-- All balances table -->
    <table class="table table-striped table-sm">
      <thead>
        <tr>
          <th>Chain</th>
          <th>Asset</th>
          <th>Balance</th>
          <th>Price (USDT)</th>
          <th>Value (USDT)</th>
          <th>24h %</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="rows.length === 0">
          <td colspan="6" class="text-center text-muted py-4">
            Connect Ledger on any chain page to load balances here.
          </td>
        </tr>
        <tr v-for="(r, i) in rows" :key="i">
          <td>
            <span class="badge" :style="`background:${r.color}; color:${r.textColor || '#fff'}; font-size:0.7rem`">
              {{ r.chainLabel }}
            </span>
          </td>
          <td><strong>{{ r.ticker }}</strong></td>
          <td>{{ r.balance }}</td>
          <td>{{ r.price !== null ? '$' + r.price : '—' }}</td>
          <td>{{ r.value !== null ? '$' + r.value : '—' }}</td>
          <td :class="r.change24h >= 0 ? 'text-success' : 'text-danger'">
            {{ r.change24h !== null ? (r.change24h >= 0 ? '+' : '') + r.change24h.toFixed(2) + '%' : '—' }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { fetchPrices, formatUsd } from '../services/priceService';
import PageLoader from '../components/PageLoader.vue';
import eventBus from '../services/eventBus';

const CHAIN_META = {
  tron:     { label: 'Tron',    color: '#FF0013', textColor: '#fff' },
  solana:   { label: 'Solana',  color: '#9945FF', textColor: '#fff' },
  ethereum: { label: 'ETH',     color: '#627EEA', textColor: '#fff' },
  bsc:      { label: 'BSC',     color: '#F0B90B', textColor: '#000' },
  polygon:  { label: 'Polygon', color: '#8247E5', textColor: '#fff' },
  bitcoin:  { label: 'Bitcoin', color: '#F7931A', textColor: '#fff' },
};

export default {
  name: 'Dashboard',
  components: { PageLoader },
  data() {
    return {
      prices: {},
      changes: {},
      lastUpdated: null,
      refreshing: false,
    };
  },
  computed: {
    wallets() {
      return this.$store ? this.$store.state.wallets : {};
    },
    rows() {
      const result = [];
      for (const [chain, meta] of Object.entries(CHAIN_META)) {
        const wallet = this.wallets[chain];
        if (!wallet || !wallet.connected || !wallet.balances.length) continue;
        for (const b of wallet.balances) {
          const raw = parseFloat(String(b.rawBalance || b.balance).replace(/,/g, '')) || 0;
          const price = this.prices[b.ticker] ?? null;
          const value = price !== null ? (raw * price).toFixed(2) : null;
          const change24h = this.changes[b.ticker] ?? null;
          result.push({
            chain,
            chainLabel: meta.label,
            color: meta.color,
            textColor: meta.textColor,
            ticker: b.ticker,
            balance: b.balance,
            price: price !== null ? price.toFixed(4) : null,
            value,
            change24h,
          });
        }
      }
      return result;
    },
    chainSummaries() {
      return Object.entries(CHAIN_META).map(([chain, meta]) => {
        const wallet = this.wallets[chain];
        const connected = wallet && wallet.connected && wallet.balances.length > 0;
        let total = null;
        if (connected) {
          let sum = 0;
          for (const b of wallet.balances) {
            const raw = parseFloat(String(b.rawBalance || b.balance).replace(/,/g, '')) || 0;
            const price = this.prices[b.ticker] ?? 0;
            sum += raw * price;
          }
          total = sum.toFixed(2);
        }
        return { chain, label: meta.label, color: meta.color, total };
      });
    },
    totalUsd() {
      return this.rows.reduce((sum, r) => sum + (r.value !== null ? parseFloat(r.value) : 0), 0);
    },
  },
  methods: {
    async refresh() {
      this.refreshing = true;
      const tickers = [...new Set(this.rows.map(r => r.ticker))];
      if (tickers.length === 0) { this.refreshing = false; return; }
      try {
        const { prices, changes } = await fetchPrices(tickers, true);
        this.prices = prices;
        this.changes = changes;
        this.lastUpdated = new Date().toLocaleTimeString();
        if (this.$store) this.$store.commit('prices/SET_PRICES', { prices, changes });
      } catch (e) {
        console.error('Price fetch error:', e);
      } finally {
        this.refreshing = false;
      }
    },
    fmtUsd(v) { return formatUsd(v); },
  },
  mounted() {
    if (this.$store) {
      this.prices = this.$store.state.prices.prices || {};
      this.changes = this.$store.state.prices.changes || {};
    }
    this.refresh();
    eventBus.$on('refresh-prices', this.refresh);
  },
  beforeDestroy() {
    eventBus.$off('refresh-prices', this.refresh);
  },
};
</script>

<style scoped>
.container-fluid { padding: 0 !important; }

h4 { font-size: 1.1rem !important; font-weight: 800 !important; color: var(--text, #c8cfe8) !important; letter-spacing: -0.01em; }

/* Total value banner */
.alert {
  background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06)) !important;
  border: 1px solid rgba(99,102,241,0.25) !important;
  border-radius: 12px !important;
  padding: 16px 20px !important;
}
.alert strong { color: var(--text-2, #8892b0) !important; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.07em; }
.total-val {
  font-size: 1.8rem !important;
  font-weight: 800 !important;
  background: linear-gradient(135deg, #818cf8, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  margin-left: 10px;
}

/* Chain summary cards */
.card {
  transition: transform 0.18s, box-shadow 0.18s !important;
}
.card:hover {
  transform: translateY(-3px) !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}
.card-body .font-weight-bold { font-size: 0.82rem !important; font-weight: 800 !important; letter-spacing: 0.04em; }
.card-body .text-muted { font-size: 0.78rem !important; margin-top: 3px; }

/* Table */
.table-sm thead th { background: var(--surface-2, #171b28) !important; }
.badge { border-radius: 6px !important; padding: 4px 8px !important; font-size: 0.68rem !important; font-weight: 700 !important; }

/* Price source label */
.price-source {
  font-size: 0.7rem;
  color: #f0b90b;
  background: rgba(240,185,11,0.12);
  border-radius: 6px;
  padding: 2px 7px;
  font-weight: 600;
  letter-spacing: 0.04em;
  vertical-align: middle;
}
</style>
